import { EmptyState } from '@agentic-flow-studio/shared/components/empty-state';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Card } from '@agentic-flow-studio/shared/ui/card';
import { Edit2, Plus, Trash2, Wrench } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function SkillsPage() {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const navigate = useNavigate();
  const basePath = `/org/${orgId}/workspace/${workspaceId}/skills`;
  const utils = trpc.useUtils();

  const { data: skills = [], isLoading } = trpc.jobPlan.skills.list.useQuery();

  const deleteSkillMutation = trpc.jobPlan.skills.delete.useMutation({
    onSuccess: () => {
      toast.success('Skill deleted');
      utils.jobPlan.skills.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Action Bar */}
      <div className="flex items-center justify-between p-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight">Skills Catalog</h1>
          <Badge variant="outline" className="text-xs">
            {skills.length} Skills
          </Badge>
        </div>

        <Button size="sm" asChild className="gap-1.5">
          <Link to={`${basePath}/new`}>
            <Plus className="h-4 w-4" />
            <span>Create Skill</span>
          </Link>
        </Button>
      </div>

      {/* Main Skills List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading skills...</div>
        ) : skills.length === 0 ? (
          <EmptyState
            icon={Wrench}
            title="No skills defined"
            description="Define capabilities, certifications, and compliance requirements needed for jobs."
            action={
              <Button size="sm" asChild>
                <Link to={`${basePath}/new`}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  <span>Create Skill</span>
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {skills.map((skill) => (
              <Card
                key={skill.id}
                className="p-3.5 flex flex-col justify-between gap-3 transition-shadow hover:shadow-md cursor-pointer group"
                onClick={() => navigate(`${basePath}/${skill.id}/edit`)}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <Badge
                      variant="outline"
                      style={{
                        backgroundColor: skill.color ? `${skill.color}20` : undefined,
                        color: skill.color || undefined,
                        borderColor: skill.color ? `${skill.color}40` : undefined,
                      }}
                      className="text-xs font-semibold px-2.5 py-0.5"
                    >
                      {skill.name}
                    </Badge>

                    <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${basePath}/${skill.id}/edit`);
                        }}
                        title="Edit Skill"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Delete skill "${skill.name}"?`)) {
                            deleteSkillMutation.mutate({ id: skill.id });
                          }
                        }}
                        title="Delete Skill"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {skill.description ? (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {skill.description}
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground/60 italic">
                      No description provided
                    </p>
                  )}

                  {skill.keywords && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/30 px-2 py-0.5 rounded font-mono truncate">
                      <span className="font-semibold text-foreground/70">Keywords:</span>
                      <span className="truncate">{skill.keywords}</span>
                    </div>
                  )}

                  {skill.exampleJobTypes && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/20 px-2 py-0.5 rounded truncate">
                      <span className="font-semibold text-foreground/70">Jobs:</span>
                      <span className="truncate">{skill.exampleJobTypes}</span>
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t border-border/40 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`${basePath}/${skill.id}/edit`);
                    }}
                  >
                    <Edit2 className="h-3 w-3" />
                    <span>Edit Skill</span>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
